import type { Bulletin } from '@shared/types/Bulletin';
import type { Ecriture } from '@shared/types/Ecriture';
import type { Salarie } from '@shared/types/Salarie';
import type { TableComptesPaie } from '@domain/comptabilite/ComptesPaie';
import { generateEcrituresPaie } from '@domain/comptabilite/EcrituresPaieGenerator';
import {
  planifierCorrectionDoublons,
  type GroupeDoublons,
  type PlanCorrectionDoublons,
} from '@domain/paie/CorrectionDoublons';
import { runAtomicWrites, type AtomicWrite } from '@application/persistence/AtomicBatch';

/** Ports minimaux (les dépôts de l'application les satisfont). */
export interface DepotBulletins {
  list(): Promise<Bulletin[]>;
  add(b: Bulletin): Promise<unknown>;
  delete(id: string): Promise<unknown>;
}
export interface DepotEcritures {
  list(): Promise<Ecriture[]>;
  add(e: Ecriture): Promise<unknown>;
  delete(id: string): Promise<unknown>;
}
export interface DepotSalaries {
  list(): Promise<Salarie[]>;
}

export interface CorrectionDoublonsDeps {
  bulletins: DepotBulletins;
  ecritures: DepotEcritures;
  salaries: DepotSalaries;
}

export interface CorrectionDoublonsOptions {
  /** Table de correspondance des comptes de paie (défaut : valeurs documentées). */
  comptesPaie?: TableComptesPaie;
  /** Périodes comptables clôturées : une période close n'est jamais modifiée. */
  periodesCloturees?: readonly string[];
  /** Limite la correction à une période ('AAAA-MM'). */
  periode?: string;
  /**
   * Sauvegarde appelée AVANT toute suppression, avec tout ce qui va disparaître (bulletins et écritures de paie
   * remplacées). Si elle échoue, rien n'est supprimé.
   */
  sauvegarder?: (aSauvegarder: SauvegardeAvantCorrection) => Promise<void> | void;
}

export interface SauvegardeAvantCorrection {
  bulletins: Bulletin[];
  ecritures: Ecriture[];
}

export interface GroupeIgnore {
  salarieId: string;
  periode: string;
  motif: string;
}

export interface ResultatCorrectionDoublons {
  plan: PlanCorrectionDoublons;
  /** Bulletins réellement supprimés. */
  supprimes: Bulletin[];
  /** Écritures de paie remplacées (supprimées, puis régénérées à partir du bulletin conservé). */
  ecrituresSupprimees: Ecriture[];
  ecrituresCreees: Ecriture[];
  /** Groupes laissés intacts, avec la raison. */
  ignores: GroupeIgnore[];
}

/** Écritures de paie d'un salarié sur une période (d'origine ou d'imputation). */
function ecrituresDuGroupe(
  ecritures: readonly Ecriture[],
  groupe: GroupeDoublons,
  salarie: Salarie | undefined,
): Ecriture[] {
  return ecritures.filter(
    (e) =>
      e.source === 'paie' &&
      (e.periode_origine ?? e.periode) === groupe.periode &&
      (e.salarie_id === groupe.salarieId ||
        (salarie !== undefined && e.matricule !== undefined && e.matricule === salarie.matricule)),
  );
}

/**
 * Supprime les doublons EXACTS de bulletins (jamais un bulletin validé ou payé) et refait les écritures de paie du
 * salarié et de la période à partir du seul bulletin conservé. Idempotent : un second passage ne trouve plus rien.
 * Les déclarations (AD-01, CNAMGS, IRPP, DTS) sont calculées à partir des bulletins : elles se corrigent d'elles-mêmes.
 * Chaque groupe est appliqué de façon atomique (retour arrière si une écriture échoue). Une période clôturée n'est
 * jamais modifiée : le groupe est laissé intact et signalé.
 */
export async function corrigerDoublonsBulletins(
  deps: CorrectionDoublonsDeps,
  options: CorrectionDoublonsOptions = {},
): Promise<ResultatCorrectionDoublons> {
  const cloturees = options.periodesCloturees ?? [];
  const [bulletins, ecritures, salaries] = await Promise.all([
    deps.bulletins.list(),
    deps.ecritures.list(),
    deps.salaries.list(),
  ]);
  const plan = planifierCorrectionDoublons(bulletins, options.periode);
  const parId = new Map(salaries.map((s) => [s.id, s]));

  const supprimes: Bulletin[] = [];
  const ecrituresSupprimees: Ecriture[] = [];
  const ecrituresCreees: Ecriture[] = [];
  const ignores: GroupeIgnore[] = [];

  interface GroupePrepare {
    groupe: GroupeDoublons;
    anciennes: Ecriture[];
    nouvelles: Ecriture[];
  }
  const prepares: GroupePrepare[] = [];
  for (const groupe of plan.groupes) {
    if (groupe.aSupprimer.length === 0) continue;
    if (cloturees.includes(groupe.periode)) {
      ignores.push({
        salarieId: groupe.salarieId,
        periode: groupe.periode,
        motif: 'période comptable clôturée : aucune modification',
      });
      continue;
    }
    const salarie = parId.get(groupe.salarieId);
    const anciennes = ecrituresDuGroupe(ecritures, groupe, salarie);
    let nouvelles: Ecriture[] = [];
    if (anciennes.length > 0) {
      if (!salarie) {
        ignores.push({
          salarieId: groupe.salarieId,
          periode: groupe.periode,
          motif: 'fiche salarié introuvable : écritures non régénérables',
        });
        continue;
      }
      // La période est ouverte (les périodes closes sont écartées plus haut) : imputation sur la période du bulletin.
      nouvelles = generateEcrituresPaie(groupe.conserve, salarie, options.comptesPaie).map((e) => ({
        ...e,
        tenant_id: groupe.conserve.tenant_id,
      }));
    }
    prepares.push({ groupe, anciennes, nouvelles });
  }

  if (prepares.length > 0 && options.sauvegarder) {
    await options.sauvegarder({
      bulletins: prepares.flatMap((p) => p.groupe.aSupprimer),
      ecritures: prepares.flatMap((p) => p.anciennes),
    });
  }

  for (const { groupe, anciennes, nouvelles } of prepares) {
    const ecrituresAtomiques: AtomicWrite[] = [
      ...groupe.aSupprimer.map((b) => ({
        table: 'bulletins',
        id: b.id,
        run: async () => {
          await deps.bulletins.delete(b.id);
        },
        rollback: async () => {
          await deps.bulletins.add(b);
        },
      })),
      ...anciennes.map((e) => ({
        table: 'ecritures',
        id: e.id,
        run: async () => {
          await deps.ecritures.delete(e.id);
        },
        rollback: async () => {
          await deps.ecritures.add(e);
        },
      })),
      ...nouvelles.map((e) => ({
        table: 'ecritures',
        id: e.id,
        run: async () => {
          await deps.ecritures.add(e);
        },
        rollback: async () => {
          await deps.ecritures.delete(e.id);
        },
      })),
    ];
    await runAtomicWrites(ecrituresAtomiques);
    supprimes.push(...groupe.aSupprimer);
    ecrituresSupprimees.push(...anciennes);
    ecrituresCreees.push(...nouvelles);
  }

  return { plan, supprimes, ecrituresSupprimees, ecrituresCreees, ignores };
}
